import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { translate, TranslocoModule } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MessagesModule } from 'primeng/messages';
import { RippleModule } from 'primeng/ripple';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { MicrophonePermissionState } from '../../../layouts/components/toolbar/helpers/enum';
import { MicrophonePermissionService } from '../../../layouts/components/toolbar/services/microphone-permission-service';

@UntilDestroy()
@Component({
  selector: 'app-microphone-permission-warning',
  standalone: true,
  imports: [ButtonModule, CommonModule, MessagesModule, RippleModule, TranslocoModule],
  templateUrl: './microphone-permission-warning.component.html',
  styleUrl: './microphone-permission-warning.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MicrophonePermissionWarningComponent implements OnInit {
  /////////////////////////////////////////////////////////////////////////////////////////////
  // State
  /////////////////////////////////////////////////////////////////////////////////////////////

  private readonly permissionState$ = new BehaviorSubject<PermissionState | null>(null);

  public readonly isRequesting$ = new BehaviorSubject<boolean>(false);

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Derived State
  /////////////////////////////////////////////////////////////////////////////////////////////

  public readonly showBanner$: Observable<boolean> = this.permissionState$.pipe(
    map((state) => state === MicrophonePermissionState.prompt),
  );

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Lifecycle
  /////////////////////////////////////////////////////////////////////////////////////////////

  constructor(
    private readonly microphonePermissionService: MicrophonePermissionService,
    private readonly messageService: MessageService,
  ) {}

  public ngOnInit(): void {
    this.microphonePermissionService
      .observeMicrophonePermission()
      .pipe(untilDestroyed(this))
      .subscribe((state) => this.permissionState$.next(state));
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Actions
  /////////////////////////////////////////////////////////////////////////////////////////////

  public async enableNotificationSounds(): Promise<void> {
    this.isRequesting$.next(true);

    try {
      const granted = await this.microphonePermissionService.requestMicrophonePermission();

      if (granted) {
        this.permissionState$.next(MicrophonePermissionState.granted);
        this.showToast('success', 'successToastSummary', 'successToastDetail');
        return;
      }

      if (await this.microphonePermissionService.isDenied()) {
        this.permissionState$.next(MicrophonePermissionState.denied);
        this.showToast('warn', 'blockedToastSummary', 'blockedToastDetail');
      }
    } finally {
      this.isRequesting$.next(false);
    }
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Private Methods
  /////////////////////////////////////////////////////////////////////////////////////////////

  private showToast(severity: 'success' | 'warn', summaryKey: string, detailKey: string): void {
    this.messageService.add({
      severity,
      summary: translate(`microphone-permission-warning.${summaryKey}`),
      detail: translate(`microphone-permission-warning.${detailKey}`),
      life: 5000,
      closable: true,
    });
  }
}
